import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MessageCircle, Send, ShoppingBag, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/contexts/I18nContext";
import AgentRoiChart from "@/components/AgentRoiChart";
import AgentIcon from "@/components/AgentIcon";
import VoiceChat from "@/components/VoiceChat";
import { buildWhatsAppLink } from "@/lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgentDetailModal({ agent, open, onOpenChange }) {
  const { t, formatPrice, lang } = useI18n();
  const [chat, setChat] = useState([]);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (agent && open) {
      setChat([{ role: "agent", text: agent.chatbot.intro }]);
      setDraft("");
    }
  }, [agent, open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat, thinking]);

  const priceFmt = useMemo(() => (agent ? formatPrice(agent.price_php) : ""), [agent, formatPrice]);

  const reply = (message) => {
    if (!agent) return "";
    const m = message.toLowerCase();
    const s = agent.chatbot.scripted;
    const hits = [
      ["hotel", "hôtel", "réserv", "voyage"], ["coord"], ["prio"], ["suivi"], ["cohorte"],
      ["leverage", "levier", "cach"], ["pitch"], ["relance"], ["fiches"], ["modération", "moderat"],
      ["tendance"], ["story"], ["moodboard", "visuel"], ["langue", "multi"], ["litige"],
      ["rgpd", "privacy"], ["reform"], ["sensible"],
    ];
    const map = {
      hotel: s.hotel, coord: s.coord, prio: s.prio, suivi: s.suivi, cohorte: s.cohorte,
      leverage: s.leverage, pitch: s.pitch, relance: s.relance, fiches: s.fiches,
      moderation: s.moderation, tendance: s.tendance, story: s.story, moodboard: s.moodboard,
      langue: s.langue, litige: s.litige, rgpd: s.rgpd, reform: s.reform, sensible: s.sensible,
    };
    for (const tags of hits) {
      for (const tag of tags) {
        if (m.includes(tag) && map[tags[0]]) return map[tags[0]];
      }
    }
    return s.default;
  };

  const send = (forced) => {
    const text = (forced ?? draft).trim();
    if (!text) return;
    setChat((c) => [...c, { role: "user", text }]);
    setDraft("");
    setThinking(true);
    setTimeout(() => {
      setChat((c) => [...c, { role: "agent", text: reply(text) }]);
      setThinking(false);
    }, 600 + Math.random() * 500);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="agent-detail-modal"
          className="bg-[#070a0c] border border-white/10 text-white max-w-4xl p-0 overflow-hidden"
        >
          {agent && (
            <div className="grid md:grid-cols-[280px_1fr]">
              <div className="p-5 border-r border-white/10 bg-black/40">
                <div className={`dncm-hud-frame ${agent.accent === "green" ? "green" : ""} rounded-lg p-3 bg-black/50`}>
                  <div className="flex items-center gap-3">
                    <span
                      className="flex items-center justify-center w-12 h-12 rounded-lg"
                      style={{
                        background: `${agent.color}1A`,
                        border: `1px solid ${agent.color}55`,
                        color: agent.color,
                        boxShadow: `0 0 22px ${agent.color}33`,
                      }}
                    >
                      <AgentIcon name={agent.icon} className="w-6 h-6" />
                    </span>
                    <div>
                      <div className="font-display text-2xl leading-none">{agent.name}</div>
                      <div className="font-mono-d text-[10px] uppercase tracking-[0.26em] text-white/55 mt-1">
                        {agent.role}
                      </div>
                    </div>
                  </div>
                </div>

                <DialogHeader className="mt-4 space-y-1">
                  <DialogTitle className="font-display text-base text-white sr-only">{agent.name}</DialogTitle>
                  <DialogDescription className="text-white/75 text-sm leading-relaxed">
                    {agent.long}
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-4">
                  <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">
                    {t("capacities_title")}
                  </div>
                  <ul className="space-y-1.5">
                    {agent.capacities.map((c) => (
                      <li key={c} className="flex items-start gap-2 text-[13px] text-white/70">
                        <span
                          className="mt-1.5 inline-block w-1 h-1 rounded-full shrink-0"
                          style={{ background: agent.color, boxShadow: `0 0 6px ${agent.color}` }}
                        />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5 rounded-lg border border-white/10 bg-black/30 p-3">
                  <AgentRoiChart agent={agent} height={120} />
                </div>
              </div>

              <div className="p-5 flex flex-col max-h-[min(85vh,720px)]">
                <div className="flex items-center justify-between">
                  <div className="font-mono-d text-[10px] uppercase tracking-[0.26em] text-white/55">
                    {lang === "fr" ? "Démo chatbot" : "Demo chat"} — {agent.name}
                  </div>
                  <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45">
                    {t("activation_label")} · <span className="text-white">{priceFmt}</span>
                  </div>
                </div>

                {agent.voice ? (
                  <div className="mt-3 flex-1 flex flex-col items-center justify-center rounded-lg border border-violet-700/30 bg-black/30">
                    <VoiceChat agentId={agent.id} sessionId={`voice-${agent.id}-web`} />
                  </div>
                ) : (
                  <>
                    <div
                      ref={scrollRef}
                      data-testid={`agent-chat-${agent.id}`}
                      className="mt-3 flex-1 overflow-y-auto rounded-lg border border-white/10 bg-black/30 p-3 space-y-2.5"
                    >
                      {chat.map((m, i) => (
                        <div
                          key={i}
                          className={`max-w-[85%] rounded-xl px-3 py-2 text-[13px] leading-relaxed ${
                            m.role === "agent" ? "dncm-bubble-agent self-start" : "dncm-bubble-user self-end ml-auto"
                          }`}
                        >
                          {m.text}
                        </div>
                      ))}
                      {thinking && (
                        <div className="dncm-bubble-agent self-start max-w-[50%] rounded-xl px-3 py-2 inline-flex items-center gap-2 text-[12px]">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          {agent.name}…
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {agent.chatbot.examples.map((ex) => (
                        <button
                          key={ex}
                          data-testid={`chat-example-${ex.slice(0, 12)}`}
                          onClick={() => send(ex)}
                          className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-white/65 hover:text-[color:var(--dncm-cyan)] px-2.5 py-1 rounded-full border border-white/10 hover:border-[color:var(--dncm-cyan)]/60 transition-colors"
                        >
                          {ex}
                        </button>
                      ))}
                    </div>

                    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex items-center gap-2">
                      <Input
                        data-testid={`chat-input-${agent.id}`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={`${t("chat_input_placeholder")} ${agent.name}…`}
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--dncm-cyan)]"
                      />
                      <button
                        type="submit"
                        data-testid={`chat-send-${agent.id}`}
                        className="inline-flex items-center justify-center w-11 h-11 rounded-md text-[#001018] dncm-btn-glow"
                        style={{ background: "linear-gradient(180deg, #6ff3ff 0%, #00cfff 60%, #00a6cf 100%)" }}
                        aria-label="Envoyer"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </form>
                  </>
                )}

                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <button
                    data-testid={`order-agent-${agent.id}`}
                    onClick={() => setOrderOpen(true)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] dncm-offer-btn"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    {t("order_btn")} ({priceFmt})
                  </button>
                  <a
                    data-testid={`whatsapp-agent-${agent.id}`}
                    href={buildWhatsAppLink(`Bonjour, je veux commander l'agent ${agent.name} (${agent.role}).`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] text-white border"
                    style={{ borderColor: "rgba(0,207,255,0.5)", background: "rgba(0,207,255,0.04)" }}
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <OrderDialog agent={agent} open={orderOpen} onOpenChange={setOrderOpen} afterSubmit={() => { setOrderOpen(false); onOpenChange(false); }} />
    </>
  );
}

function OrderDialog({ agent, open, onOpenChange, afterSubmit }) {
  const { t, formatPrice, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agent || !form.name.trim()) {
      toast.error(lang === "fr" ? "Merci d'indiquer votre nom." : "Please share your name.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        message: form.message.trim() || null,
        source: "order",
        agent: agent.name,
      });
      toast.success(t("success_order"));
      setForm({ name: "", email: "", message: "" });
      afterSubmit?.();
    } catch {
      toast.error(lang === "fr" ? "Envoi impossible. Réessayez ou utilisez WhatsApp." : "Could not send. Try again or use WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#070a0c] border border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">
            {agent ? `${t("order_btn")} · ${agent.name}` : t("order_btn")}
          </DialogTitle>
          <DialogDescription className="text-white/65">
            {agent ? `${agent.role} — ${formatPrice(agent.price_php)}` : ""}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="space-y-1.5">
            <Label htmlFor="ord-name" className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("name")}</Label>
            <Input id="ord-name" data-testid="order-name" value={form.name} onChange={update("name")}
              className="bg-black/40 border-white/15 text-white focus-visible:ring-[color:var(--dncm-cyan)]" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ord-email" className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("email")}</Label>
            <Input id="ord-email" type="email" data-testid="order-email" value={form.email} onChange={update("email")}
              className="bg-black/40 border-white/15 text-white focus-visible:ring-[color:var(--dncm-cyan)]" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ord-msg" className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("message")}</Label>
            <Input id="ord-msg" data-testid="order-message" value={form.message} onChange={update("message")}
              className="bg-black/40 border-white/15 text-white focus-visible:ring-[color:var(--dncm-cyan)]" />
          </div>
          <button type="submit" data-testid="order-submit" disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] dncm-offer-btn disabled:opacity-60">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {t("confirm_order")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
